// @ts-nocheck
/**
 * Swap seeds in this bracket — bracket positions only.
 *
 * Deliberately narrower than SwapTeamsDialog: nothing outside the bracket
 * moves. Pools, pool games and pool results are left exactly as they are.
 */
import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2, ArrowLeftRight } from 'lucide-react';
import { toast } from 'sonner';
import { invokeEdgeFunction } from '@/lib/invokeEdgeFunction';

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  eventId: string;
  division: string;
  /** Teams currently placed in the bracket: { registration_id, team_name } */
  teams: Array<{ registration_id: string; team_name: string }>;
  onChanged: () => void;
}

export default function SwapSeedsDialog({ open, onOpenChange, eventId, division, teams, onChanged }: Props) {
  const [a, setA] = useState('');
  const [b, setB] = useState('');
  const [preview, setPreview] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => { if (open) { setA(''); setB(''); setPreview(null); } }, [open]);

  const call = async (confirm: boolean) => {
    const { data: res, error } = await invokeEdgeFunction('rle-operator-schedule-manual', {
      body: {
        action: 'bracket_swap_seeds',
        event_id: eventId,
        division,
        registration_id_a: a,
        registration_id_b: b,
        confirm,
      },
    });
    if (error || !res?.ok) {
      throw new Error(error?.message || res?.error?.message || 'Could not work out the exchange.');
    }
    return res.data;
  };

  useEffect(() => {
    if (!open || !a || !b || a === b) { setPreview(null); return; }
    let cancelled = false;
    setLoading(true);
    call(false)
      .then((d) => { if (!cancelled) setPreview(d); })
      .catch((e) => { if (!cancelled) { setPreview(null); toast.error(e.message); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, a, b, eventId, division]);

  const confirm = async () => {
    setApplying(true);
    try {
      const d = await call(true);
      toast.success(d?.message || 'Bracket positions exchanged.');
      onOpenChange(false);
      onChanged();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setApplying(false);
    }
  };

  const picker = (value: string, onChange: (v: string) => void, exclude: string, label: string) => (
    <div className="space-y-1">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger><SelectValue placeholder="Choose a team" /></SelectTrigger>
        <SelectContent>
          {teams.filter((t) => t.registration_id && t.registration_id !== exclude).map((t) => (
            <SelectItem key={t.registration_id} value={t.registration_id}>{t.team_name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowLeftRight className="h-4 w-4" /> Swap seeds in this bracket
          </DialogTitle>
          <DialogDescription>
            Two teams exchange their bracket positions in {division}. Pools, pool games and pool
            results are not touched. Use <strong>Swap two teams</strong> instead if you mean to move
            a team's whole tournament.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 sm:grid-cols-2">
          {picker(a, setA, b, 'Team')}
          {picker(b, setB, a, 'Exchanges with')}
        </div>

        {loading && (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Working out the exchange…
          </p>
        )}

        {preview && (
          <div className="rounded-md border p-3 text-sm">
            <p>{preview.message}</p>
            <ul className="mt-2 space-y-0.5 text-xs text-muted-foreground">
              {(preview.moves ?? []).map((m: any) => (
                <li key={m.game_id}>{m.label}: {m.before}</li>
              ))}
            </ul>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={applying}>Cancel</Button>
          <Button onClick={confirm} disabled={!preview || applying}>
            {applying && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Exchange positions
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
